import * as React from 'react';
import { DeliveryStatus, SalesOrderType, Res, Permission, SalesOrderDTO, PrintPreviewDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import * as Images from 'pos/Images';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import * as UI from 'core/ui';
import { Button, Col, Cols, Flex, TextBoxButton } from 'core/ui';
import Orders from './Orders';
import { Format } from 'core/ui';
import { canTransitionToAccept, canTransitionToCancel, canTransitionToDelivered, canTransitionToDelivering, canTransitionToPrepare, canTransitionToPrepared, canClose, canReopenOrder } from './transitions';
import { CashdrawerFacade, PrintFacade, TakeOutOrderFacade } from 'pos/facades';
import { SalesOrderModel, toModel } from './SalesOrderModel';
import { Command } from 'pos/common';
import { SaleModuleNextAction } from '../helpers/SaleModuleNextAction';
import { BarcodeEncoder, TakeOutOrdersMonitor } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { SvgIcon } from 'core/ui-next/chakra-components';
import { legacyControlsFor } from 'core/ui-next/controls';

const { Form, TextBox } = UI.controlsFor<State>();
const { DatePicker } = legacyControlsFor<State>();

type Props = {
	initialDate: Date
} & Modal.Actions<SaleModuleNextAction>;

const NO_ORDER_SELECTED = -1;

type State = {
	orders: SalesOrderModel[];
	date: Date;
	searchText: string;
	showPreview: boolean;
	selectedOrderId: number;
	selectedDeliveryStatus: DeliveryStatus[];
	selectedOrderType: SalesOrderType[];
	sortField: keyof SalesOrderModel;
	sortDirection: 'asc' | 'desc';
	preview: Nullable<PrintPreviewDTO>;
};

const allStatuses = [
	DeliveryStatus.Accepted,
	DeliveryStatus.Delivered,
	DeliveryStatus.Delivering,
	DeliveryStatus.New,
	DeliveryStatus.Prepared,
	DeliveryStatus.Preparing,
	DeliveryStatus.Rejected
];

const takeOutTypes: (SalesOrderType.Delivery | SalesOrderType.TakeAway)[] = [
	SalesOrderType.Delivery,
	SalesOrderType.TakeAway
];

let initialStatuses = [DeliveryStatus.New, DeliveryStatus.Accepted, DeliveryStatus.Preparing, DeliveryStatus.Prepared, DeliveryStatus.Delivering];
let initialTypes = [SalesOrderType.TakeAway, SalesOrderType.Delivery];
let initialShowPreview = false;

class ShowTakeOutOrdersScreen extends PosScreenClassComponent<Props, State> {

	private searchInput = React.createRef<UI.TextBox<State>>();
	private timer: Nullable<NodeJS.Timeout> = null;
	private disposeReader: () => void = () => { };

	async onComponentDidMount(): Promise<void> {

		this.timer = setInterval(this.reloadOrders, 30000);
		this.on('pos:takeout-order:operation-failed', async error => {
			await Message.show(error.msg);
			await this.reloadOrders();
		});

		this.disposeReader = UI.BarcodeReader.start(this.onBarcodeRead);
		await this.reloadOrders();

		this.on('pos:sales-order:operation-failed', async error => {
			await Message.show(error.msg);
			await this.reloadOrders();
		});
	}

	onComponentWillUnmount(): void {
		if (this.timer)
			clearInterval(this.timer);

		initialStatuses = this.state.selectedDeliveryStatus;
		initialTypes = this.state.selectedOrderType;
		initialShowPreview = this.state.showPreview;

		this.disposeReader();
	}

	// Filtro siempre por todos los estados y tipos porque hago filtro en capa de presentación para poder mostrar los contadores en cada estado
	private reloadOrders = async () => {
		const orders = await TakeOutOrderFacade.getByDeliveryDate(this.state.date, takeOutTypes, allStatuses);
		this.setState({ orders: orders.map(toModel) });
	};

	protected getScreenId(): string {
		return 'SHOW_TAKE_OUT_ORDERS';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_TAKE_OUT_ORDERS();
	}

	protected getImage(): string | SvgIcon {
		return Images.ShowTakeOutOrders;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		const order = this.state.orders.find(x => x.Id === this.state.selectedOrderId);

		return [
			this.cancelButton(),
			{ title: Res.Web.SHOW_TAKE_OUT_ORDERS_PREPARATION_SUMMARY(), image: Images.ShowSalesOrdersPreparation, action: async () => { await Command.execute('showSaleOrdersPreparations', this.getFilteredOrders()); } },
			canReopenOrder(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_REOPEN_ORDER(), image: Images.ReopenTicket, action: this.reopenSelectedOrder } : null,
			canTransitionToAccept(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_ACCEPT_ORDER(), image: Images.Ok, action: async () => { await this.doOnSelectedOrder(this.accept); } } : null,
			canTransitionToCancel(order) ? { title: cancelText(order), image: cancelImage(order), action: async () => { await this.doOnSelectedOrder(this.cancelOrder); } } : null,
			canTransitionToPrepare(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_PREPARE_ORDER(), image: Images.PrepareTicket, action: async () => { await this.doOnSelectedOrder(this.prepare); } } : null,
			canTransitionToPrepared(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_PREPARED_ORDER(), image: Images.PreparedOrder, action: async () => { await this.doOnSelectedOrder(this.markAsPrepared); } } : null,
			this.getDeliveryingOrChangeDeliveryPersonButton(order),
			canTransitionToDelivered(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_DELIVERED_ORDER(), image: Images.DeliveredOrder, action: async () => { await this.doOnSelectedOrder(this.markAsDelivered); } } : null,
			showPrint(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_PRINT_ORDER(), image: Images.PrintTicket, action: async () => { await this.doOnSelectedOrder(this.print); } } : null,
			canClose(order) ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_CLOSE_ORDER(), image: Images.CloseTicket, action: async () => { await this.doOnSelectedOrder(this.closeOrder); } } : null

		];
	}

	private getDeliveryingOrChangeDeliveryPersonButton = (order?: SalesOrderModel) => canTransitionToDelivering(order)
		? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_DELIVERING_ORDER(), image: Images.DeliveringOrder, action: async () => { await this.doOnSelectedOrder(this.deliver); } }
		: order?.DeliveryStatus === DeliveryStatus.Delivering ? { title: Res.Web.SHOW_TAKE_OUT_ORDERS_CHANGE_DELIVERY_PERSON(), image: Images.ChangeDeliveryPerson, action: async () => { await this.doOnSelectedOrder(this.changeDeliveryPerson); } } : null;

	protected useKeypad() {
		return false;
	}

	private reopenSelectedOrder = async () => {
		const order = this.getSelectedOrder();
		if (order === null)
			return;

		const result = await Command.execute('reopenSalesOrder', order);
		if (result.ok)
			this.close({ type: 'loadTicket', ticketId: result.value.reopenedTicketId });
	};

	private doOnSelectedOrder = async (action: () => Promise<Result>) => {
		if (this.state.selectedOrderId === NO_ORDER_SELECTED)
			return;

		await action();
		await this.reloadOrders();
	};

	private getSelectedOrder = (): Nullable<SalesOrderModel> => {
		return this.state.orders.find(o => o.Id === this.state.selectedOrderId) || null;
	};

	private accept = async () => Command.execute('acceptTakeOutOrder', this.state.selectedOrderId);

	private cancelOrder = async () => {
		const order = this.getSelectedOrder()!;
		if (order.DeliveryStatus !== DeliveryStatus.New) {
			return await Command.execute('cancelTakeOutOrder', { order, allowCancelWithPreviousPayments: true });
		} else {
			return await Command.execute('rejectTakeOutOrder', this.state.selectedOrderId);
		}
	};

	private prepare = async () => Command.execute('prepareSalesOrder', this.getSelectedOrder()!);

	private markAsPrepared = async () => Command.execute('markTakeOutOrderAsPrepared', this.state.selectedOrderId);

	private deliver = async () => Command.execute('deliverTakeOutOrder', this.state.selectedOrderId, this.getSelectedOrder()?.DeliveryPerson);

	private changeDeliveryPerson = async () => Command.execute('changeTakeOutOrderDeliveryPerson', this.state.selectedOrderId, this.getSelectedOrder()?.DeliveryPerson, 'WARN_IF_NO_DELIVERY_PERSONS');

	private markAsDelivered = async () => Command.execute('markTakeOutOrderAsDelivered', this.state.selectedOrderId);

	private print = async () => Command.execute('printTakeOutOrder', this.getSelectedOrder()!);

	private closeOrder = async () => {

		const closeResult = await Command.execute('closeTakeOutOrder', this.getSelectedOrder()!);

		if (!closeResult.ok)
			return Result.error();

		await this.reloadOrders();

		const newOrder = this.state.orders.find(o => o.Id === this.state.selectedOrderId);
		if (newOrder?.DeliveryStatus === DeliveryStatus.Delivering && newOrder?.DeliveryPerson === '')
			return await Command.execute('changeTakeOutOrderDeliveryPerson', this.state.selectedOrderId, newOrder?.DeliveryPerson, 'IGNORE_IF_NO_DELIVERY_PERSONS');

		return Result.ok();
	};

	private onBarcodeRead = async (barcode: string) => {
		const idResult = await BarcodeEncoder.tryDecodeId(BarcodeEncoder.EntityType.SalesOrder, barcode);
		if (!idResult.ok) {
			await Message.show(BarcodeEncoder.isQRCode(barcode)
				? Res.Msg.MSG_QR_IS_NOT_A_VALID_PENDING_SALES_ORDER_QR(barcode)
				: Res.Msg.MSG_BARCODE_IS_NOT_A_VALID_PENDING_SALES_ORDER_BARCODE(barcode));
			return;
		}

		const salesOrder = await TakeOutOrderFacade.getById(idResult.value);
		if (!salesOrder || salesOrder.Id === 0 || !this.state.orders.find(x => x.Id === salesOrder.Id)) {
			await Message.show(Res.Msg.MSG_SALES_ORDER_NOT_FOUND());
			return;
		}

		const previews = await PrintFacade.getSalesOrderPreview(salesOrder.Id);
		this.setState({ selectedOrderId: salesOrder.Id, preview: previews[0] });
	};

	createInitialState(props: Props): Nullable<State> {
		return {
			orders: [],
			date: props.initialDate,
			searchText: '',
			showPreview: initialShowPreview,
			selectedOrderId: NO_ORDER_SELECTED,
			selectedDeliveryStatus: initialStatuses,
			selectedOrderType: initialTypes,
			sortField: 'Number',
			sortDirection: 'asc',
			preview: null
		};
	}

	componentDidUpdate() {
		const selectionIsStillShown = this.state.selectedOrderId === NO_ORDER_SELECTED || this.getFilteredOrders().some(x => x.Id === this.state.selectedOrderId);
		if (!selectionIsStillShown)
			this.setState({
				selectedOrderId: NO_ORDER_SELECTED,
				preview: null
			});
	}

	private toggleType = (current: boolean, type: SalesOrderType) => this.setState({
		selectedOrderType: current
			? this.state.selectedOrderType.filter(x => x !== type)
			: [...this.state.selectedOrderType, type]
	});

	private toggleStatus = async (current: boolean, status: DeliveryStatus) => this.setState({
		selectedDeliveryStatus: current
			? this.state.selectedDeliveryStatus.filter(x => x !== status)
			: [...this.state.selectedDeliveryStatus, status]
	});

	private sort = (sortField: keyof SalesOrderModel) => {
		const sortDirection = this.state.sortField === sortField && this.state.sortDirection === 'asc' ? 'desc' : 'asc';
		this.setState({ sortField, sortDirection });
	};

	private selectSalesOrder = async (order: SalesOrderModel) => {
		const previews = await PrintFacade.getSalesOrderPreview(order.Id);
		this.setState({ selectedOrderId: order.Id, preview: previews[0] });
	};

	private getFilteredOrders(): SalesOrderModel[] {
		const orders = this.state.orders.filter(x => {
			const validStatus = this.state.selectedDeliveryStatus.includes(x.DeliveryStatus);
			const validType = this.state.selectedOrderType.includes(x.Type);
			const validText = !this.state.searchText || [
				// Busco por cualquiera de estos campos
				x.SerialNumber, x.Customer.FiscalName, x.CustomerPhone, UI.Format.asMoney(x.NetAmount), Format.asDeliveryDate(x.DeliveryDate!)
			].some(field => field.toLowerCase().includes(this.state.searchText.toLowerCase()));
			return validStatus && validType && validText;
		});

		return this.state.sortField ? orders.sortBy(x => (x as any)[this.state.sortField], this.state.sortDirection) : orders;
	}

	private onClearSearchText = () => {
		// No hago un remount porque no estoy manteniendo sincronizado el form con los controles
		this.searchInput && this.searchInput.current!.setValue('');
		this.setState({ searchText: '' });
	};

	private onDateChanged = (date: Nullable<Date>) => {
		if (!date)
			return;
		this.setState({ date }, () => this.reloadOrders());
	};

	protected getContent(): React.ReactNode {
		const takeOutColor = ThemeManager.getCurrentTheme().value === 'light' ? '#4C566A' : '#FFFFFF';

		return <Flex.Horizontal>
			<Flex.Item size='*'>
				<Flex.Vertical>
					<Flex.Item className='p-xs'>
						<Form model={this.state}>
							<Cols className='m-b-n'>
								<Col>
									<DatePicker linkTo={x => x.date} className='text-center' tabIndex={-1} onValueChanged={this.onDateChanged} />
								</Col>
								<Col width='*' className='m-l-lg m-r-lg'>
									<TextBox ref={this.searchInput} linkTo={x => x.searchText} placeholder={Res.Web.SHOW_TAKE_OUT_ORDERS_SEARCH_TEXT_PLACEHOLDER()} onValueChange={searchText => this.setState({ searchText })}>
										<TextBoxButton iconClassName='fa fa-times text-info' onClick={this.onClearSearchText} />
									</TextBox>
								</Col>
								<Col>
									<SortMenu sortField={this.state.sortField} sortDirection={this.state.sortDirection} sort={this.sort} />
								</Col >
								<Col className='m-l-sm'>
									<Button onClick={() => this.setState({ showPreview: !this.state.showPreview })}>
										<span className={`text-info fa ${this.state.showPreview ? 'fa-chevron-right' : 'fa-chevron-left'}`} />
									</Button>
								</Col>
							</Cols>
						</Form>
					</Flex.Item>
					<Flex.Item className='p-l-sm p-r-sm p-b-sm border-bottom'>
						<Flex.Horizontal style={{ justifyContent: 'space-between' }}>
							<Flex.Item>
								<ButtonGroup previewIsShown={this.state.showPreview} >
									<TypeButton orders={this.state.orders} image={<SvgIcon src={Images.TakeAway} color={takeOutColor} width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_TYPE_TAKE_AWAY()} type={SalesOrderType.TakeAway} selectedOrderType={this.state.selectedOrderType} toggle={this.toggleType} />
									<TypeButton orders={this.state.orders} image={<SvgIcon src={Images.Delivery} color={takeOutColor} width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_TYPE_DELIVERY()} type={SalesOrderType.Delivery} selectedOrderType={this.state.selectedOrderType} toggle={this.toggleType} />
								</ButtonGroup>
							</Flex.Item>
							<Flex.Item>
								<ButtonGroup previewIsShown={this.state.showPreview} >
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutNew} color='#61AAED' width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_NEW()} status={DeliveryStatus.New} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutAccepted} color='#75CA8D' width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_ACCEPTED()} status={DeliveryStatus.Accepted} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutRejected} color="#FF8A95" width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_REJECTED()} status={DeliveryStatus.Rejected} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutPreparing} color="#FFBA7B" width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_PREPARING()} status={DeliveryStatus.Preparing} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutPrepared} color="#FFBA7B" width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_PREPARED()} status={DeliveryStatus.Prepared} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutDelivering} color="#C291F3" width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_DELIVERING()} status={DeliveryStatus.Delivering} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
									<StatusButton orders={this.state.orders} image={<SvgIcon src={Images.TakeOutDelivered} color="#C291F3" width={'40px'} height={'40px'} />} text={Res.Web.SHOW_TAKE_OUT_ORDERS_STATUS_DELIVERED()} status={DeliveryStatus.Delivered} selectedDeliveryStatus={this.state.selectedDeliveryStatus} toggle={this.toggleStatus} />
								</ButtonGroup>
							</Flex.Item>
						</Flex.Horizontal>
					</Flex.Item>
					<Flex.Item size='*' className='bg-light' style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fill, minmax(min(600px, 100%), 1fr))',
						gridAutoRows: 'min-content'
					}}>
						<Orders orders={this.getFilteredOrders()} selectedOrderId={this.state.selectedOrderId} selectOrder={this.selectSalesOrder} />
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>

			<Flex.Item size={this.state.showPreview ? '425px' : '0'} className='border-left'
				style={{ transition: 'width 0.2s ease-in-out', borderWidth: this.state.showPreview ? '1px' : '0px' }}>
				<Flex.Vertical>
					<Flex.Item size='*'>
						{this.state.preview ?
							<div className='m-sm '>
								<UI.TemplatePrintPreview preview={this.state.preview} />
							</div>
							: <div className='font-trans m-t-xxxl text-18 text-center'>{Res.Web.SHOW_TAKE_OUT_ORDERS_NO_ORDER_SELECTED()}</div>}
					</Flex.Item>
				</Flex.Vertical>
			</Flex.Item>

		</Flex.Horizontal >;
	}
}

const cancelText = (order?: SalesOrderDTO) => order && order.DeliveryStatus === DeliveryStatus.New ? Res.Web.SHOW_TAKE_OUT_ORDERS_REJECT_ORDER() : Res.Web.SHOW_TAKE_OUT_ORDERS_CANCEL_ORDER();
const cancelImage = (order?: SalesOrderDTO) => order && order.DeliveryStatus === DeliveryStatus.New ? Images.RejectOrder : Images.CancelOrder;
const showPrint = (order?: SalesOrderDTO) => !!order;

const ButtonGroup = ({ previewIsShown, children }: { previewIsShown: boolean, children: React.ReactNode[] }) =>
	<Flex.Item>
		<Flex.Horizontal>
			{children.map((x, idx) => <Flex.Item key={idx} size={previewIsShown ? '60' : '75'}>{x}</Flex.Item>)}
		</Flex.Horizontal>
	</Flex.Item>;

const StatusButton = ({ orders, selectedDeliveryStatus, status, text, image, toggle }: { orders: SalesOrderModel[], selectedDeliveryStatus: DeliveryStatus[], status: DeliveryStatus, text: string, image: React.ReactNode, toggle: (active: boolean, status: DeliveryStatus) => void }): React.ReactNode => {
	const quantity = orders.filter(x => x.DeliveryStatus === status).length;
	const active = selectedDeliveryStatus.includes(status);
	return <FilterButton text={text} image={image} quantity={quantity} active={active} onClick={() => toggle(active, status)} />;
};

const TypeButton = ({ orders, selectedOrderType, type, text, image, toggle }: { orders: SalesOrderModel[], selectedOrderType: SalesOrderType[], type: SalesOrderType, text: string, image: React.ReactNode, toggle: (active: boolean, type: SalesOrderType) => void }): React.ReactNode => {
	const quantity = orders.filter(x => x.Type === type).length;
	const active = selectedOrderType.includes(type);
	return <FilterButton text={text} image={image} quantity={quantity} active={active} onClick={() => toggle(active, type)} />;
};

const SortMenu = ({ sortField, sortDirection, sort }: { sortField: keyof SalesOrderModel, sortDirection: 'asc' | 'desc', sort: (sortField: keyof SalesOrderModel) => void }) => {

	const item = (key: number, text: string, sortField: keyof SalesOrderModel) => {
		return <li key={key} className='p-xs' onClick={() => sort(sortField)}>{text}</li>;
	};

	const sortFields: [keyof SalesOrderModel, string][] = [
		['Number', Res.Web.SHOW_TAKE_OUT_ORDERS_SORT_BY_NUMBER()],
		['DeliveryDate', Res.Web.SHOW_TAKE_OUT_ORDERS_SORT_BY_DATE()],
		['DeliveryStatus', Res.Web.SHOW_TAKE_OUT_ORDERS_SORT_BY_STATUS()],
		['NetAmount', Res.Web.SHOW_TAKE_OUT_ORDERS_SORT_BY_NET_AMOUNT()],
		['DeliveryAddressSummary', Res.Web.SHOW_TAKE_OUT_ORDERS_SORT_BY_ADDRESS()]
	];

	return <div className='btn-group dropdown'>
		<a data-toggle='dropdown' className='btn btn-default dropdown-toggle '>
			{sortFields.find(([sField]) => sField === sortField)![1]}
			<i className={`text-info fa ${sortDirection === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc'} m-l-xs`}></i>
		</a>
		<ul className='dropdown-menu dropdown-menu-right'>
			{sortFields.map(([sortField, label], idx) => item(idx, label, sortField))}
		</ul>
	</div>;
};

const FilterButton = ({ text, quantity, active, image, onClick }: { text?: string, quantity?: number, active?: boolean, image: React.ReactNode, onClick?: () => void }) => {
	return <Flex.Vertical style={{ alignItems: 'center', opacity: active ? 1 : .5, position: 'relative' }} onClick={onClick}>
		<Flex.Item>{image}</Flex.Item>
		<Flex.Item className='text-12 text-center'>
			{text}
			{quantity === undefined ? '' : <span className='badge border' style={{ position: 'absolute', top: 0, right: 5 }}>{quantity}</span>}
		</Flex.Item>
	</Flex.Vertical>;
};

declare global {
	interface CommandType {
		showTakeOutOrders: () => Promise<Result<SaleModuleNextAction>>
	}
}

Command.register('showTakeOutOrders', Preconditions.and(Preconditions.ensure(Permission.SalesOrder_ShowTakeOutOrders), Preconditions.ensureIsNotLite), async (): Promise<Result<SaleModuleNextAction>> => {
	const businessDay = await CashdrawerFacade.getCurrentBusinessDay();
	if (!businessDay)
		return Result.error();

	const result = await Modal.show(ShowTakeOutOrdersScreen, { initialDate: businessDay });

	void TakeOutOrdersMonitor.forceUpdate();

	return result;
});
